import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { AlertTriangle, CheckCircle2, FileWarning, History, Link2, Loader2, RotateCcw, Save } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorState, LoadingState } from "@/components/ui/states";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";
import { formatDateTime } from "@/utils/dateUtils";
import { financeApi } from "@/services/finance-api";
import {
  useAtivarVersaoTemplate,
  useCatalogoDoModelo,
  usePropostas,
  useSalvarVersaoTemplate,
  useTemplate,
} from "@/hooks/use-finance-propostas";
import { EditorDeSecoes } from "@/components/finance/modelo/EditorDeSecoes";
import { ehModeloDeInvite } from "@/types/finance";
import type { SecaoDoModelo, TemplateTipo, VarianteDoModelo } from "@/types/finance";

const ESPERA_PARA_PREVIA_MS = 700;
const DADOS_DE_EXEMPLO = "exemplo";

const TIPOS: Array<{ id: TemplateTipo; rotulo: string; descricao: string }> = [
  { id: "proposta", rotulo: "Proposta", descricao: "Documento para a agência" },
  { id: "invite", rotulo: "Invite", descricao: "Convite para o freelancer" },
  { id: "proposta_fixa", rotulo: "Proposta de vaga fixa", descricao: "Recrutamento para vaga fixa, para a agência" },
  { id: "invite_fixa", rotulo: "Invite de vaga fixa", descricao: "Convite para o profissional contratado" },
];

type Origem = { tipo: "versao"; id: string; versao: number } | { tipo: "padrao" };

function descreverOrigem(origem: Origem | null): string {
  if (!origem) return "";
  return origem.tipo === "padrao" ? "padrão do sistema" : `versão ${origem.versao}`;
}

const VARIANTES: Array<{ id: VarianteDoModelo; rotulo: string; descricao: string }> = [
  { id: "gota_serena", rotulo: "Gota Serena", descricao: "Proposta comercial e ordem de serviço" },
  { id: "corisco", rotulo: "Corisco", descricao: "Agenciamento, Corisco e Faísca" },
  { id: "faisca", rotulo: "Faísca", descricao: "Agenciamento, a Faísca fatura a agência" },
];

const PARAMETRO_EMPRESA = "empresa";
const PARAMETRO_DOCUMENTO = "documento";

function varianteDaUrl(valor: string | null): VarianteDoModelo {
  return VARIANTES.find((item) => item.id === valor)?.id ?? "gota_serena";
}

function tipoDaUrl(valor: string | null): TemplateTipo {
  return TIPOS.find((item) => item.id === valor)?.id ?? "proposta";
}

function linkDoModelo(variante: VarianteDoModelo, tipo: TemplateTipo): string {
  const url = new URL(window.location.href);
  url.search = "";
  url.searchParams.set("aba", "modelo");
  url.searchParams.set(PARAMETRO_EMPRESA, variante);
  url.searchParams.set(PARAMETRO_DOCUMENTO, tipo);
  return url.toString();
}

/** Cada modelo tem o seu link: ?aba=modelo&empresa=corisco&documento=invite. */
export function TemplatesTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const variante = varianteDaUrl(searchParams.get(PARAMETRO_EMPRESA));
  const tipo = tipoDaUrl(searchParams.get(PARAMETRO_DOCUMENTO));
  const [editorAlterado, setEditorAlterado] = useState(false);

  const abrir = (proximaVariante: VarianteDoModelo, proximoTipo: TemplateTipo) => {
    if (proximaVariante === variante && proximoTipo === tipo) return;
    if (editorAlterado && !window.confirm("Descartar as alterações que ainda não foram salvas?")) return;
    setEditorAlterado(false);
    setSearchParams(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        proximo.set(PARAMETRO_EMPRESA, proximaVariante);
        proximo.set(PARAMETRO_DOCUMENTO, proximoTipo);
        return proximo;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        {VARIANTES.map((empresa) => (
          <div key={empresa.id} className="rounded-lg border">
            <div className="border-b px-4 py-2.5">
              <p className="text-sm font-medium">{empresa.rotulo}</p>
              <p className="text-xs text-muted-foreground">{empresa.descricao}</p>
            </div>
            <ul className="divide-y">
              {TIPOS.map((documento) => (
                <LinhaDoModelo
                  key={documento.id}
                  variante={empresa.id}
                  tipo={documento.id}
                  rotulo={documento.rotulo}
                  descricao={documento.descricao}
                  selecionado={variante === empresa.id && tipo === documento.id}
                  onAbrir={() => abrir(empresa.id, documento.id)}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Cada proposta usa o modelo da empresa que fatura a agência, ou o escolhido nela. O que muda aqui vale para as
        próximas propostas; para mudar só uma, edite direto no documento dela.
      </p>
      <EditorDeModelo
        key={`${variante}-${tipo}`}
        tipo={tipo}
        variante={variante}
        onAlteradoChange={setEditorAlterado}
      />
    </div>
  );
}

interface LinhaDoModeloProps {
  variante: VarianteDoModelo;
  tipo: TemplateTipo;
  rotulo: string;
  descricao: string;
  selecionado: boolean;
  onAbrir: () => void;
}

function LinhaDoModelo({ variante, tipo, rotulo, descricao, selecionado, onAbrir }: LinhaDoModeloProps) {
  const { toast } = useToast();
  const modeloQuery = useTemplate(tipo, variante);
  const emUso = modeloQuery.data?.em_uso;
  const rotuloEmUso = !emUso ? null : emUso.origem === "padrao" ? "padrão do sistema" : `versão ${emUso.versao} em uso`;

  const copiarLink = async () => {
    const link = linkDoModelo(variante, tipo);
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: "Link do modelo copiado" });
    } catch {
      toast({ variant: "destructive", title: "Não foi possível copiar o link", description: link });
    }
  };

  return (
    <li className={cn("flex items-center gap-2 pr-2 transition-colors", selecionado ? "bg-accent" : "hover:bg-muted/50")}>
      <button
        type="button"
        aria-current={selecionado ? "page" : undefined}
        onClick={onAbrir}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-3 border-l-2 px-4 py-2.5 text-left",
          selecionado ? "border-primary" : "border-transparent",
        )}
      >
        <span className="min-w-0 flex-1">
          <span className={cn("block text-sm", selecionado && "font-medium")}>{rotulo}</span>
          <span className="block truncate text-xs text-muted-foreground">{descricao}</span>
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {modeloQuery.isLoading ? <Skeleton className="inline-block h-3 w-20" /> : rotuloEmUso}
        </span>
      </button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={copiarLink}
        aria-label={`Copiar link do modelo ${rotulo}`}
        title="Copiar link deste modelo"
      >
        <Link2 className="h-4 w-4" />
      </Button>
    </li>
  );
}

interface EditorDeModeloProps {
  tipo: TemplateTipo;
  variante: VarianteDoModelo;
  onAlteradoChange: (alterado: boolean) => void;
}

function EditorDeModelo({ tipo, variante, onAlteradoChange }: EditorDeModeloProps) {
  const { toast } = useToast();
  const modeloQuery = useTemplate(tipo, variante);
  const catalogoQuery = useCatalogoDoModelo(tipo, variante);
  const salvar = useSalvarVersaoTemplate(tipo, variante);
  const ativar = useAtivarVersaoTemplate(tipo, variante);
  const propostasQuery = usePropostas({ page: 1, page_size: 30 });

  const [secoes, setSecoes] = useState<SecaoDoModelo[]>([]);
  const [secoesSalvas, setSecoesSalvas] = useState("[]");
  const [versaoAntigaEmHtml, setVersaoAntigaEmHtml] = useState(false);
  const [origem, setOrigem] = useState<Origem | null>(null);
  const [carregandoConteudo, setCarregandoConteudo] = useState(false);
  const [observacao, setObservacao] = useState("");
  const [fontePrevia, setFontePrevia] = useState(DADOS_DE_EXEMPLO);
  const [previa, setPrevia] = useState<{ html?: string; erro?: string; carregando: boolean }>({ carregando: false });

  const secoesSerializadas = useMemo(() => JSON.stringify(secoes), [secoes]);
  const alterado = !versaoAntigaEmHtml && secoesSerializadas !== secoesSalvas;

  useEffect(() => {
    onAlteradoChange(alterado);
  }, [alterado, onAlteradoChange]);

  const reportarErro = useCallback(
    (erro: unknown) => {
      const formatado = formatApiError(erro);
      toast({ variant: "destructive", title: formatado.title, description: formatado.detail });
    },
    [toast],
  );

  const carregar = async (proxima: Origem) => {
    if (alterado && !window.confirm("Descartar as alterações que ainda não foram salvas?")) return;
    setCarregandoConteudo(true);
    try {
      const resposta = await financeApi.getTemplateConteudo(tipo, variante, proxima.tipo === "versao" ? proxima.id : null);
      const carregadas = resposta.formato === "blocos" && resposta.secoes ? resposta.secoes : [];
      setVersaoAntigaEmHtml(resposta.formato !== "blocos");
      setSecoes(carregadas);
      setSecoesSalvas(JSON.stringify(carregadas));
      setOrigem(proxima);
    } catch (erro) {
      reportarErro(erro);
    } finally {
      setCarregandoConteudo(false);
    }
  };

  useEffect(() => {
    if (!modeloQuery.data || origem) return;
    const ativa = modeloQuery.data.versoes.find((versao) => versao.ativo);
    carregar(ativa ? { tipo: "versao", id: ativa.id, versao: ativa.versao } : { tipo: "padrao" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeloQuery.data]);

  useEffect(() => {
    if (versaoAntigaEmHtml || secoes.length === 0) return;
    setPrevia((atual) => ({ ...atual, carregando: true }));
    const temporizador = setTimeout(() => {
      financeApi
        .previaTemplate(tipo, {
          secoes,
          ...(fontePrevia !== DADOS_DE_EXEMPLO ? { proposta_id: fontePrevia } : {}),
        })
        .then((resposta) => setPrevia({ html: resposta.html, carregando: false }))
        .catch((erro) =>
          setPrevia((atual) => ({ html: atual.html, erro: formatApiError(erro).detail, carregando: false })),
        );
    }, ESPERA_PARA_PREVIA_MS);
    return () => clearTimeout(temporizador);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secoesSerializadas, fontePrevia, versaoAntigaEmHtml, tipo]);

  useEffect(() => {
    const avisarAoSair = (evento: BeforeUnloadEvent) => {
      if (alterado) evento.preventDefault();
    };
    window.addEventListener("beforeunload", avisarAoSair);
    return () => window.removeEventListener("beforeunload", avisarAoSair);
  }, [alterado]);

  const salvarVersao = useCallback(async () => {
    if (!alterado || salvar.isPending) return;
    try {
      const resumo = await salvar.mutateAsync({ secoes, observacao: observacao.trim() || undefined });
      const nova = resumo.versoes.find((versao) => versao.ativo);
      setSecoesSalvas(secoesSerializadas);
      if (nova) setOrigem({ tipo: "versao", id: nova.id, versao: nova.versao });
      setObservacao("");
      toast({
        title: `Versão ${nova?.versao ?? ""} salva e em uso`,
        description: "Os próximos documentos gerados já usam este modelo. Os já gerados não mudam.",
      });
    } catch (erro) {
      reportarErro(erro);
    }
  }, [alterado, salvar, secoes, observacao, secoesSerializadas, toast, reportarErro]);

  useEffect(() => {
    const atalhoSalvar = (evento: KeyboardEvent) => {
      if ((evento.metaKey || evento.ctrlKey) && evento.key.toLowerCase() === "s") {
        evento.preventDefault();
        salvarVersao();
      }
    };
    window.addEventListener("keydown", atalhoSalvar);
    return () => window.removeEventListener("keydown", atalhoSalvar);
  }, [salvarVersao]);

  const colocarEmUso = async (versaoId: string | null) => {
    try {
      await ativar.mutateAsync(versaoId);
      toast({ title: versaoId ? "Versão em uso" : "Voltou para o modelo padrão do sistema" });
    } catch (erro) {
      reportarErro(erro);
    }
  };

  if (modeloQuery.isLoading) return <LoadingState variant="list" count={3} />;
  if (modeloQuery.isError || !modeloQuery.data) return <ErrorState onRetry={() => modeloQuery.refetch()} />;

  const modelo = modeloQuery.data;
  const emUso = modelo.em_uso;
  const rotuloEmUso = emUso.origem === "padrao" ? "padrão do sistema" : `versão ${emUso.versao}`;
  const rotuloDoDocumento = ehModeloDeInvite(tipo) ? "nos invites" : "nas propostas";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border bg-muted/30 px-4 py-3 text-sm">
        <span className="flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          Em uso {rotuloDoDocumento}: <strong>{rotuloEmUso}</strong>
        </span>
        <span className="text-muted-foreground">
          Editando: <strong className="text-foreground">{descreverOrigem(origem)}</strong>
        </span>
        {alterado && (
          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
            Alterações não salvas
          </Badge>
        )}
      </div>

      <div className="grid items-start gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card className="min-w-0">
          <CardHeader className="space-y-3 pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Documento</CardTitle>
              <p className="text-xs text-muted-foreground">
                Clique no texto para editar. Passe o mouse numa parte para mover ou tirar.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                aria-label="O que mudou nesta versão"
                value={observacao}
                onChange={(evento) => setObservacao(evento.target.value)}
                placeholder="O que mudou (opcional), ex.: novo texto de aceite"
                className="h-9 min-w-[220px] flex-1"
                disabled={versaoAntigaEmHtml}
              />
              <Button className="gap-1.5" disabled={!alterado || salvar.isPending} onClick={salvarVersao}>
                {salvar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar nova versão
              </Button>
              <Button
                variant="outline"
                className="gap-1.5"
                disabled={!alterado}
                onClick={() => setSecoes(JSON.parse(secoesSalvas) as SecaoDoModelo[])}
              >
                <RotateCcw className="h-4 w-4" />
                Descartar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="bg-muted/40 px-2 py-6 sm:px-10">
            {carregandoConteudo || catalogoQuery.isLoading ? (
              <Skeleton className="mx-auto h-[70vh] w-full max-w-[794px]" />
            ) : versaoAntigaEmHtml ? (
              <div className="mx-auto flex max-w-[794px] items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <FileWarning className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="space-y-2">
                  <p>
                    Esta é uma versão antiga, feita antes do editor visual. Ela ainda pode ser colocada em uso, mas não
                    dá para editar aqui.
                  </p>
                  <Button size="sm" variant="outline" onClick={() => carregar({ tipo: "padrao" })}>
                    Começar a partir do padrão do sistema
                  </Button>
                </div>
              </div>
            ) : (
              <EditorDeSecoes tipo={tipo} secoes={secoes} onChange={setSecoes} catalogo={catalogoQuery.data} />
            )}
            <p className="mx-auto mt-3 max-w-[794px] text-[11px] text-muted-foreground">
              Salvar cria uma versão nova e ela passa a valer para os próximos documentos. Cmd/Ctrl+S também salva.
            </p>
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-4 2xl:sticky 2xl:top-4">
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                Prévia
                {previa.carregando && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              </CardTitle>
              <Select value={fontePrevia} onValueChange={setFontePrevia}>
                <SelectTrigger className="h-8 w-[280px] text-xs" aria-label="Dados usados na prévia">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DADOS_DE_EXEMPLO}>Com dados de exemplo</SelectItem>
                  {(propostasQuery.data?.items.length ?? 0) > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-[11px] font-normal text-muted-foreground">
                        {ehModeloDeInvite(tipo) ? "Invite do primeiro profissional da proposta" : "Com os dados da proposta"}
                      </SelectLabel>
                      {(propostasQuery.data?.items ?? []).map((proposta) => (
                        <SelectItem key={proposta.id} value={proposta.id}>
                          {proposta.codigo ?? "Rascunho"} · {proposta.agencia.nome}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="space-y-2 bg-muted/40 p-4">
              {previa.erro && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>Não foi possível montar a prévia: {previa.erro}</span>
                </div>
              )}
              <iframe
                title="Prévia do modelo"
                srcDoc={previa.html}
                sandbox=""
                className={cn(
                  "mx-auto block h-[70vh] w-full max-w-[794px] rounded-sm bg-white shadow-md ring-1 ring-black/5",
                  previa.erro && "opacity-50",
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="h-4 w-4" />
                Versões
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y rounded-md border text-sm">
                {modelo.versoes.map((versao) => (
                  <li key={versao.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <span className="w-10 font-medium">v{versao.versao}</span>
                    {versao.ativo && (
                      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">
                        Em uso
                      </Badge>
                    )}
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                      {versao.observacao ?? "Sem descrição"}
                      {" · "}
                      {versao.autor ?? "sistema"}
                      {versao.criado_em ? ` · ${formatDateTime(versao.criado_em)}` : ""}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => carregar({ tipo: "versao", id: versao.id, versao: versao.versao })}
                    >
                      Abrir no editor
                    </Button>
                    {!versao.ativo && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        disabled={ativar.isPending}
                        onClick={() => colocarEmUso(versao.id)}
                      >
                        Usar esta
                      </Button>
                    )}
                  </li>
                ))}
                <li className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <span className="w-10 font-medium">-</span>
                  {emUso.origem === "padrao" && (
                    <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">
                      Em uso
                    </Badge>
                  )}
                  <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">Padrão do sistema</span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => carregar({ tipo: "padrao" })}>
                    Abrir no editor
                  </Button>
                  {emUso.origem !== "padrao" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={ativar.isPending}
                      onClick={() => colocarEmUso(null)}
                    >
                      Usar este
                    </Button>
                  )}
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
